import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const { FiArrowLeft, FiArrowRight, FiShield, FiEdit3, FiHeart } = FiIcons;

const values = [
  { icon: FiEdit3, t: 'Your voice, not ours', d: 'AI should amplify how you already write, never flatten it into a house style. Voice DNA exists to keep your book sounding like you.' },
  { icon: FiShield, t: 'Your work stays yours', d: 'You own every word and every export. Your manuscript and Voice Memory are private and never used to train models for anyone else.' },
  { icon: FiHeart, t: 'Finish, publish — and get read', d: 'A tool should get you past "done." KDP-ready formatting, clean export, a path to sell — and social marketing in your voice, so readers actually find your book.' }
];

const AboutPage = () => {
  const structuredData = {
    '@context': 'https://schema.org', '@type': 'AboutPage',
    name: 'About ManuscriptSmith',
    description: 'Why ManuscriptSmith exists: an AI writing studio built by an author to help writers keep their own voice and publish KDP-ready.',
    url: 'https://manuscriptsmith.com/about'
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="About ManuscriptSmith — Built by an Author, for Authors"
        description="ManuscriptSmith is an AI writing studio built by author Vitaly Kirkpatrick to help writers keep their own voice, stay KDP-ready, and actually finish and publish their books."
        keywords="about manuscriptsmith, ai writing studio founder, vitaly kirkpatrick, author voice ai"
        canonicalUrl="https://manuscriptsmith.com/about"
        structuredData={structuredData}
      />

      <main>
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">
            Built by an author, <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">for authors</span>
          </h1>
          <p className="text-xl text-gray-600">
            ManuscriptSmith started with a simple frustration: every AI writing tool made my book sound like
            everyone else's.
          </p>
        </section>

        {/* Story */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 space-y-4 text-gray-700 leading-8">
            <p>
              I'm Vitaly Kirkpatrick. Like a lot of writers, I wanted help finishing a book - mine is a memoir about coming to America and finding my love, Kimball - without handing my voice over to a machine. I tried the popular AI tools.
              They were fast, and they were useful for ideas. But everything they produced came back in the same
              smooth, anonymous voice. My readers would have known in a paragraph that it wasn't me.
            </p>
            <p>
              The other half of the problem came after the writing: formatting. Getting a manuscript to pass Kindle
              Direct Publishing meant wrestling with trim sizes, margins, and metadata in a separate tool - and still
              getting kicked back. Writing in one app, organizing in another, prompting a chatbot in a third, then
              fighting an exporter: that's not a writing studio, that's a tab problem.
            </p>
            <p>
              So we built the thing I actually wanted. <strong>Author Voice DNA</strong> learns how <em>you</em> write
              and keeps every suggestion in your voice. <strong>Live KDP Preflight</strong> catches formatting issues
              as you type, so you publish on the first try. And when the book is done, you can export it clean, share
              it from your own site, or even turn it into an audiobook - all in one place. And because writing a book and
              <em> selling</em> one are different battles, it even turns your finished book into on-brand social posts in
              your voice - so it gets read, not just published.
            </p>
            <p>
              My editor, Linda, uses it to ship short ebooks to her Patreon. Novelists use it to keep a series
              consistent. I use it to finally finish my own second book — the story of finding Kimball. The goal is always the same: help you
              write like yourself, and actually publish.
            </p>
            <p className="text-gray-500 italic">— Vitaly Kirkpatrick, Author & Founder</p>
          </div>
        </section>

        {/* Values */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">What we believe</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {values.map((v, i) => (
                <motion.div key={v.t} className="p-6 rounded-2xl border border-gray-200 bg-gradient-to-br from-parchment-50 to-white"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center mb-4">
                    <SafeIcon icon={v.icon} className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{v.t}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{v.d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">Write your book in your own voice</h2>
            <p className="text-lg opacity-90 mb-8">Bring a draft or begin fresh.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50">
                Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/editor" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25">
                Try the live editor
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AboutPage;
